// src/InstallationManagementModal.js
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import "./InstallationManagementModal.css";
import logger from "./utils/logger";
import config from "./config";

const MARKET_TYPES = [
  { value: "COMMERCIAL_OFFICE", label: "Commercial Office" },
  { value: "DATA_CENTER", label: "Data Center" },
  { value: "EDUCATION_K12", label: "Education K-12" },
  { value: "EDUCATION_HIGHER_ED", label: "Education Higher Ed" },
  { value: "FOOD_SERVICE", label: "Food Service" },
  { value: "GOVT_PUBLIC_BUILDING", label: "Government/Public Building" },
  { value: "HEALTHCARE", label: "Healthcare" },
  { value: "LODGING_HOSPITALITY", label: "Lodging/Hospitality" },
  { value: "MULTIFAMILY_RESIDENCE", label: "Multifamily Residence" },
  { value: "OTHERS", label: "Others" },
  { value: "RELIGIOUS", label: "Religious" },
  { value: "RETAIL", label: "Retail" },
  { value: "WAREHOUSE", label: "Warehouse" },
];

const InstallationManagementModal = ({
  isOpen,
  onClose,
  theme,
  installations = [],
  currentInstallationId,
  organizationId,
  userProfile,
  getAccessToken,
  onInstallationsChanged,
  onSwitchInstallation,
  onChangeInstallation, // Function to trigger the change installation screen
  devices = [],
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    marketType: "MULTIFAMILY_RESIDENCE",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: string }
  const [pendingSwitch, setPendingSwitch] = useState(null); // { installationId, name }
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // { installation, deviceCount }
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedInstallationId, setCopiedInstallationId] = useState(null);
  const isDeletingCurrentRef = useRef(false); // Persist through re-renders
  const [creationModal, setCreationModal] = useState({
    isOpen: false,
    isLoading: false,
    result: null,
  }); // Installation creation modal
  const [deletionModal, setDeletionModal] = useState({
    isOpen: false,
    isLoading: false,
    isRefreshing: false,
    result: null,
    isDeletingCurrent: false,
  }); // Installation deletion modal
  const prevIsOpenRef = useRef(isOpen);

  // Handle copy installation ID
  const handleCopyInstallationId = async (installationId) => {
    try {
      await navigator.clipboard.writeText(installationId);
      setCopiedInstallationId(installationId);
      setTimeout(() => setCopiedInstallationId(null), 2000);
    } catch (err) {
      logger.error("Failed to copy installation ID:", err);
    }
  };

  // Log installations prop changes
  useEffect(() => {
    logger.log("[InstallationManagementModal] Installations prop updated:", {
      organizationId,
      installationCount: installations.length,
      installationNames: installations.map((i) => i.name),
    });
  }, [installations, organizationId]);

  // Track if isOpen prop changes
  useLayoutEffect(() => {
    if (prevIsOpenRef.current !== isOpen) {
      logger.log("[Modal isOpen changed]", {
        from: prevIsOpenRef.current,
        to: isOpen,
        isDeletingCurrent: isDeletingCurrentRef.current,
      });
      prevIsOpenRef.current = isOpen;
    }
  }, [isOpen]);

  // Force re-render when isDeletingCurrent changes to ensure DOM stays updated
  useLayoutEffect(() => {
    if (deletionModal.isDeletingCurrent || isDeletingCurrentRef.current) {
      logger.log("[useLayoutEffect] isDeletingCurrent is true, checking DOM");
      const messageDiv = document.querySelector(
        ".deleting-current-installation-message"
      );
      logger.log("[useLayoutEffect] Message div exists:", !!messageDiv);
    }
  }, [deletionModal.isDeletingCurrent]);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen && !loading) {
        // If delete confirmation is open, close it instead of the main modal
        if (deleteConfirmation) {
          setDeleteConfirmation(null);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, loading, onClose, deleteConfirmation]);

  // Enter key handler for creation modal
  useEffect(() => {
    const handleEnterKey = (event) => {
      if (
        event.key === "Enter" &&
        creationModal.isOpen &&
        !creationModal.isLoading &&
        creationModal.result
      ) {
        event.preventDefault();
        // On success, show switch prompt
        if (creationModal.result.type === "success") {
          setPendingSwitch({
            installationId: creationModal.result.installationId,
            name: creationModal.result.installationName,
          });
        }
        setCreationModal({
          isOpen: false,
          isLoading: false,
          result: null,
        });
      }
    };

    if (creationModal.isOpen) {
      document.addEventListener("keydown", handleEnterKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [creationModal]);

  // Enter key handler for deletion modal
  useEffect(() => {
    const handleEnterKey = (event) => {
      if (
        event.key === "Enter" &&
        deletionModal.isOpen &&
        !deletionModal.isLoading &&
        !deletionModal.isRefreshing &&
        deletionModal.result
      ) {
        event.preventDefault();
        // If deleted current installation, close modal and trigger change installation
        if (deletionModal.result.isDeletingCurrent) {
          setDeletionModal({
            isOpen: false,
            isLoading: false,
            isRefreshing: false,
            result: null,
            isDeletingCurrent: false,
          });
          onClose();
          if (onChangeInstallation) {
            onChangeInstallation(true); // Pass true to indicate forced change (deleted current)
          }
        } else {
          setDeletionModal({
            isOpen: false,
            isLoading: false,
            isRefreshing: false,
            result: null,
            isDeletingCurrent: false,
          });
        }
      }
    };

    if (deletionModal.isOpen) {
      document.addEventListener("keydown", handleEnterKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [deletionModal, onClose, onChangeInstallation]);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setShowCreateForm(false);
      setEditingInstallation(null);
      setFormData({ name: "", marketType: "MULTIFAMILY_RESIDENCE" });
      setMessage(null);
      setPendingSwitch(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    logger.log("[handleClose] Called", {
      loading,
      isDeletingCurrentRef: isDeletingCurrentRef.current,
      stackTrace: new Error().stack,
    });
    // Don't allow closing while deleting current installation
    if (!loading && !isDeletingCurrentRef.current) {
      logger.log("[handleClose] Calling parent onClose()");
      onClose();
    } else if (isDeletingCurrentRef.current) {
      logger.log(
        "[handleClose] Blocked close - currently deleting current installation"
      );
    }
  };

  // Validate installation name (alphanumeric and spaces only)
  const validateInstallationName = (name) => {
    const validNamePattern = /^[a-zA-Z0-9 ]+$/;
    return validNamePattern.test(name);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCreateClick = () => {
    setShowCreateForm(true);
    setEditingInstallation(null);
    setFormData({ name: "", marketType: "MULTIFAMILY_RESIDENCE" });
    setMessage(null);
  };

  const handleEditClick = (installation) => {
    setEditingInstallation(installation);
    setShowCreateForm(false);
    setFormData({
      name: installation.name,
      marketType: installation.marketType || "MULTIFAMILY_RESIDENCE",
    });
    setMessage(null);
  };

  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingInstallation(null);
    setFormData({ name: "", marketType: "MULTIFAMILY_RESIDENCE" });
    setMessage(null);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();

    // Validate installation name
    if (!validateInstallationName(formData.name)) {
      showMessage(
        "error",
        "Site name can only contain letters, numbers, and spaces"
      );
      return;
    }

    logger.log("[handleCreateSubmit] Starting installation creation", {
      name: formData.name,
      marketType: formData.marketType,
    });

    setLoading(true);
    setMessage(null);
    setCreationModal({ isOpen: true, isLoading: true, result: null });
    logger.log("[handleCreateSubmit] Creation modal opened with spinner");

    try {
      const accessToken = await getAccessToken();

      logger.log("[handleCreateSubmit] Making API call to create installation");
      const response = await fetch(
        `${config.apiEndpoint}/create-installation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": config.apiKey,
          },
          body: JSON.stringify({
            orgId: organizationId,
            name: formData.name,
            marketType: formData.marketType,
            accessToken,
          }),
        }
      );

      const result = await response.json();
      logger.log("[handleCreateSubmit] API response received:", {
        success: result.success,
      });

      if (result.success) {
        logger.log(
          "[InstallationManagement] Installation created:",
          result.installation
        );
        logger.log(
          "[handleCreateSubmit] Created for organizationId:",
          organizationId
        );

        // Reset form
        setFormData({ name: "", marketType: "MULTIFAMILY_RESIDENCE" });
        setShowCreateForm(false);

        // Refresh installations list FIRST so new installation appears in UI
        logger.log("[handleCreateSubmit] Refreshing installations list");
        logger.log(
          "[handleCreateSubmit] Current organizationId before refresh:",
          organizationId
        );
        await onInstallationsChanged();
        logger.log("[handleCreateSubmit] Installations refreshed");
        logger.log(
          "[handleCreateSubmit] Current organizationId after refresh:",
          organizationId
        );

        // Show success in modal AFTER list is refreshed
        logger.log("[handleCreateSubmit] Setting success state in modal");
        setCreationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: "success",
            message: `Site "${result.installation.name}" created successfully!`,
            installationId: result.installation.installationId,
            installationName: result.installation.name,
          },
        });
      } else {
        logger.error("[InstallationManagement] Create failed:", result.message);
        setCreationModal({
          isOpen: true,
          isLoading: false,
          result: {
            type: "error",
            message: result.message || "Failed to create site",
          },
        });
      }
    } catch (error) {
      logger.error("[InstallationManagement] Create error:", error);
      setCreationModal({
        isOpen: true,
        isLoading: false,
        result: {
          type: "error",
          message: error.message || "An error occurred",
        },
      });
    } finally {
      setLoading(false);
      logger.log("[handleCreateSubmit] Creation process completed");
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    // Validate installation name
    if (!validateInstallationName(formData.name)) {
      showMessage(
        "error",
        "Site name can only contain letters, numbers, and spaces"
      );
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const accessToken = await getAccessToken();

      const requestBody = {
        installationId: editingInstallation.installationId,
        name: formData.name,
        marketType: formData.marketType,
        accessToken,
      };

      logger.log("[InstallationManagement] Update request body:", {
        installationId: requestBody.installationId,
        name: requestBody.name,
        marketType: requestBody.marketType,
      });

      const response = await fetch(
        `${config.apiEndpoint}/update-installation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": config.apiKey,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();

      if (result.success) {
        logger.log(
          "[InstallationManagement] Installation updated:",
          result.installation
        );
        showMessage(
          "success",
          `Site "${result.installation.name}" updated successfully!`
        );

        // Reset form
        setEditingInstallation(null);
        setFormData({ name: "", marketType: "MULTIFAMILY_RESIDENCE" });

        // Refresh installations list
        await onInstallationsChanged();
      } else {
        logger.error("[InstallationManagement] Update failed:", result.message);
        showMessage("error", result.message || "Failed to update site");
      }
    } catch (error) {
      logger.error("[InstallationManagement] Update error:", error);
      showMessage("error", error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (installation) => {
    const deviceCount = getDeviceCountForInstallation(
      installation.installationId
    );

    // Show confirmation modal
    setDeleteConfirmation({ installation, deviceCount });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const { installation } = deleteConfirmation;
    const isDeletingCurrentInstallation =
      installation.installationId === currentInstallationId;

    logger.log("[confirmDelete] Starting delete process", {
      installationId: installation.installationId,
      isDeletingCurrent: isDeletingCurrentInstallation,
      currentInstallationId,
    });

    setLoading(true);
    setMessage(null);
    setDeleteConfirmation(null); // Close confirmation modal
    setDeletionModal({
      isOpen: true,
      isLoading: true,
      isRefreshing: false,
      result: null,
      isDeletingCurrent: isDeletingCurrentInstallation,
      installationName: installation.name,
    });

    try {
      const accessToken = await getAccessToken();

      const response = await fetch(
        `${config.apiEndpoint}/delete-installation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": config.apiKey,
          },
          body: JSON.stringify({
            installationId: installation.installationId,
            accessToken,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        logger.log(
          "[InstallationManagement] Installation deleted:",
          installation.installationId
        );

        // Show refreshing state
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: true,
          result: null,
          isDeletingCurrent: isDeletingCurrentInstallation,
          installationName: installation.name,
        });

        // Refresh installations list
        await onInstallationsChanged();

        // Show success
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: false,
          result: {
            type: "success",
            message: `Site "${installation.name}" deleted successfully!`,
            isDeletingCurrent: isDeletingCurrentInstallation,
          },
          isDeletingCurrent: isDeletingCurrentInstallation,
          installationName: installation.name,
        });

        setLoading(false);
      } else {
        logger.error("[InstallationManagement] Delete failed:", result.message);
        setDeletionModal({
          isOpen: true,
          isLoading: false,
          isRefreshing: false,
          result: {
            type: "error",
            message: result.message || "Failed to delete site",
          },
          isDeletingCurrent: false,
          installationName: installation.name,
        });
        setLoading(false);
      }
    } catch (error) {
      logger.error("[InstallationManagement] Delete error:", error);
      setDeletionModal({
        isOpen: true,
        isLoading: false,
        isRefreshing: false,
        result: {
          type: "error",
          message: error.message || "An error occurred",
        },
        isDeletingCurrent: false,
        installationName: installation.name,
      });
      setLoading(false);
    }
  };

  const handleSwitchToNewInstallation = (shouldSwitch) => {
    if (shouldSwitch && pendingSwitch && onSwitchInstallation) {
      // Use the context method to switch installation
      const targetInstallation = installations.find(
        (i) => i.installationId === pendingSwitch.installationId
      );
      if (targetInstallation) {
        // Add organizationId to the installation object so AppContent knows which org it belongs to
        const installationWithOrg = {
          ...targetInstallation,
          orgId: organizationId,
        };
        logger.log(
          "[handleSwitchToNewInstallation] Switching to installation with org:",
          { installation: installationWithOrg, organizationId }
        );
        onSwitchInstallation(installationWithOrg);
        // Issue 5: Close the main modal only when switching
        onClose();
      }
    }
    // Issue 5: Don't close modal if user chose \"Not Now\"
    setPendingSwitch(null);
  };

  const getDeviceCountForInstallation = (installationId) => {
    return devices.filter((d) => d.installationId === installationId).length;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  if (!isOpen) return null;

  const hasInstallations = installations && installations.length > 0;

  return (
    <div
      className={`installation-management-modal-overlay ${theme}`}
      onClick={handleClose}
    >
      <div
        className={`installation-management-modal ${theme}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Site Management</h2>
          <button
            className="close-x-button"
            onClick={handleClose}
            disabled={loading}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Message Display */}
          {message && (
            <div className={`message-banner ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Deleting current installation message */}
          {(() => {
            const shouldShow =
              isDeletingCurrentRef.current || deletionModal.isDeletingCurrent;
            logger.log("[Modal Render] Deleting message check:", {
              shouldShow,
              isDeletingCurrentRef: isDeletingCurrentRef.current,
              isDeletingCurrent: deletionModal.isDeletingCurrent,
              loading,
            });

            if (shouldShow) {
              logger.log("[Modal Render] RENDERING DELETING MESSAGE DIV");

              // Check DOM immediately after React commits
              Promise.resolve().then(() => {
                const messageDiv = document.querySelector(
                  ".deleting-current-installation-message"
                );
                logger.log(
                  "[DOM Check - Microtask] Element exists:",
                  !!messageDiv
                );
              });

              // Use longer timeout to check if element persists
              setTimeout(() => {
                const messageDiv = document.querySelector(
                  ".deleting-current-installation-message"
                );
                if (messageDiv) {
                  const styles = window.getComputedStyle(messageDiv);
                  const rect = messageDiv.getBoundingClientRect();
                  logger.log("[DOM Check - 50ms] Deleting message element:", {
                    exists: true,
                    display: styles.display,
                    visibility: styles.visibility,
                    opacity: styles.opacity,
                    zIndex: styles.zIndex,
                    position: styles.position,
                    width: rect.width,
                    height: rect.height,
                    top: rect.top,
                    left: rect.left,
                    inViewport:
                      rect.top >= 0 &&
                      rect.left >= 0 &&
                      rect.bottom <= window.innerHeight &&
                      rect.right <= window.innerWidth,
                  });
                  logger.log("[DOM Check - 50ms] Parent modal-body:", {
                    className: messageDiv.parentElement?.className,
                    display: window.getComputedStyle(messageDiv.parentElement)
                      .display,
                    visibility: window.getComputedStyle(
                      messageDiv.parentElement
                    ).visibility,
                  });
                } else {
                  logger.error(
                    "[DOM Check - 50ms] Deleting message element NOT FOUND in DOM!"
                  );
                  logger.log("[DOM Check - 50ms] Current modal content:", {
                    modalExists: !!document.querySelector(
                      ".installation-management-modal"
                    ),
                    modalBodyExists: !!document.querySelector(".modal-body"),
                    allClassesInModalBody: Array.from(
                      document.querySelectorAll(".modal-body *")
                    ).map((el) => el.className),
                  });
                }
              }, 50);

              return (
                <div
                  className="deleting-current-installation-message"
                  ref={(el) => {
                    if (el) {
                      logger.log(
                        "[React Ref] Deleting message div MOUNTED in DOM"
                      );
                    } else {
                      logger.log(
                        "[React Ref] Deleting message div UNMOUNTED from DOM"
                      );
                    }
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p>
                    Current site deleted. Redirecting to site
                    selector...
                  </p>
                </div>
              );
            }
            return null;
          })()}

          {/* Debug logging */}
          {(() => {
            logger.log("[Modal Render] State check:", {
              isDeletingCurrent: deletionModal.isDeletingCurrent,
              isDeletingCurrentRef: isDeletingCurrentRef.current,
              loading,
              showDeleteMessage: isDeletingCurrentRef.current,
            });
            return null;
          })()}

          {/* Create/Edit Form */}
          {(() => {
            const shouldHide =
              isDeletingCurrentRef.current || deletionModal.isDeletingCurrent;
            logger.log("[Modal Render] Create/Edit Form check:", {
              shouldHide,
              showCreateForm,
              editingInstallation,
              willRender:
                !shouldHide && (showCreateForm || editingInstallation),
            });

            if (!shouldHide && (showCreateForm || editingInstallation)) {
              logger.log("[Modal Render] RENDERING CREATE/EDIT FORM");
              return (
                <div className="installation-form">
                  <h3>
                    {editingInstallation
                      ? "Update Site"
                      : "Create New Site"}
                  </h3>
                  <form
                    onSubmit={
                      editingInstallation
                        ? handleUpdateSubmit
                        : handleCreateSubmit
                    }
                  >
                    <div className="form-group">
                      <label htmlFor="install-name">Site Name *</label>
                      <input
                        id="install-name"
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        disabled={loading}
                        placeholder="Enter site name"
                        autoFocus
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="market-type">Market Type *</label>
                      <select
                        id="market-type"
                        value={formData.marketType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            marketType: e.target.value,
                          })
                        }
                        required
                        disabled={loading}
                      >
                        {MARKET_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-buttons">
                      <button
                        type="submit"
                        className="btn-submit"
                        disabled={loading}
                      >
                        {loading
                          ? "Processing..."
                          : editingInstallation
                          ? "Update"
                          : "Create"}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={handleCancelForm}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              );
            }
            return null;
          })()}

          {/* Installation List */}
          {(() => {
            const shouldHide =
              isDeletingCurrentRef.current || deletionModal.isDeletingCurrent;
            logger.log("[Modal Render] Installation List check:", {
              shouldHide,
              showCreateForm,
              editingInstallation,
              willRender:
                !shouldHide && !showCreateForm && !editingInstallation,
            });

            if (!shouldHide && !showCreateForm && !editingInstallation) {
              logger.log("[Modal Render] RENDERING INSTALLATION LIST");
              return (
                <>
                  <div className="installations-header">
                    <h3>Sites</h3>
                    <button
                      className="btn-create"
                      onClick={handleCreateClick}
                      disabled={loading}
                    >
                      + Create Site
                    </button>
                  </div>

                  {/* Search Box */}
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search sites by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="search-clear-button"
                        onClick={() => setSearchTerm("")}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {!hasInstallations ? (
                    <div className="empty-state">
                      <p>No sites found.</p>
                    </div>
                  ) : (
                    <div className="installations-table-container">
                      <table className="installations-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Site ID</th>
                            <th>Market Type</th>
                            <th>Devices</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Filter and sort installations
                            const filteredInstallations = installations.filter(
                              (installation) => {
                                if (!searchTerm) return true;
                                const search = searchTerm.toLowerCase();
                                return (
                                  installation.name
                                    .toLowerCase()
                                    .includes(search) ||
                                  installation.installationId
                                    .toLowerCase()
                                    .includes(search)
                                );
                              }
                            );
                            return [...filteredInstallations].sort((a, b) =>
                              a.name.localeCompare(b.name)
                            );
                          })().map((installation) => {
                            const deviceCount = getDeviceCountForInstallation(
                              installation.installationId
                            );
                            const isCurrent =
                              installation.installationId ===
                              currentInstallationId;
                            const canDelete = deviceCount === 0;

                            return (
                              <tr
                                key={installation.installationId}
                                className={
                                  isCurrent ? "current-installation" : ""
                                }
                              >
                                <td>
                                  <div className="installation-name-container">
                                    <button
                                      className="installation-name-link"
                                      onClick={() => {
                                        // Switch to this installation and close modal
                                        if (onSwitchInstallation) {
                                          onSwitchInstallation(installation);
                                        }
                                        onClose();
                                      }}
                                      title="Go to this site"
                                      disabled={loading || isCurrent}
                                    >
                                      {installation.name}
                                    </button>
                                  </div>
                                  <div className="installation-badges-row">
                                    <span
                                      className={`subscription-badge ${
                                        installation.subscriptionStatus ===
                                        "SUBSCRIBED"
                                          ? "subscribed"
                                          : "not-subscribed"
                                      }`}
                                      title={
                                        installation.subscriptionStatus ===
                                        "SUBSCRIBED"
                                          ? "Webhook subscription active"
                                          : "Webhook not subscribed"
                                      }
                                    >
                                      {installation.subscriptionStatus ===
                                      "SUBSCRIBED"
                                        ? "✓ Subscribed for Events"
                                        : "○ Not Subscribed for Events"}
                                    </span>
                                    {isCurrent && (
                                      <span className="current-badge">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="installation-id">
                                  <div className="installation-id-container">
                                    <span className="installation-id-text">
                                      {installation.installationId}
                                    </span>
                                    <button
                                      className="btn-copy-installation-id"
                                      onClick={() =>
                                        handleCopyInstallationId(
                                          installation.installationId
                                        )
                                      }
                                      title="Copy Site ID"
                                      disabled={loading}
                                    >
                                      {copiedInstallationId ===
                                      installation.installationId
                                        ? "✓"
                                        : "📋"}
                                    </button>
                                  </div>
                                </td>
                                <td>{installation.marketType || "N/A"}</td>
                                <td>{deviceCount}</td>
                                <td>{formatDate(installation.createdAt)}</td>
                                <td className="actions-cell">
                                  <div className="actions-container">
                                    <div className="action-buttons-wrapper">
                                      <button
                                        className="btn-edit"
                                        onClick={() =>
                                          handleEditClick(installation)
                                        }
                                        disabled={loading}
                                        title="Update site"
                                      >
                                        Update
                                      </button>
                                      <button
                                        className="btn-delete"
                                        onClick={() =>
                                          handleDelete(installation)
                                        }
                                        disabled={loading || !canDelete}
                                        title={
                                          canDelete
                                            ? "Delete site"
                                            : "Cannot delete site with devices"
                                        }
                                      >
                                        Delete
                                      </button>
                                    </div>
                                    {!canDelete && (
                                      <div className="delete-disabled-note">
                                        (has devices, cannot be deleted)
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            }
            return null;
          })()}
        </div>

        {/* Modal Footer with Cancel Button */}
        {!isDeletingCurrentRef.current && !deletionModal.isDeletingCurrent && (
          <div className="modal-actions">
            <button
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Switch Installation Modal */}
      {pendingSwitch && (
        <div className="switch-installation-overlay" onClick={handleClose}>
          <div
            className="switch-installation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="switch-installation-header">
              <h3>Switch Site</h3>
            </div>
            <div className="switch-installation-body">
              <p>
                Site <strong>"{pendingSwitch.name}"</strong> has been
                created successfully!
              </p>
              <p>Would you like to switch to this site now?</p>
            </div>
            <div className="switch-installation-actions">
              <button
                className="cancel-button"
                onClick={() => handleSwitchToNewInstallation(false)}
                disabled={loading}
              >
                Not Now
              </button>
              <button
                className="switch-confirm-button"
                onClick={() => handleSwitchToNewInstallation(true)}
                disabled={loading}
              >
                Switch Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="delete-confirmation-overlay" onClick={handleClose}>
          <div
            className="delete-confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="delete-confirmation-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="delete-confirmation-body">
              <p>
                Are you sure you want to delete{" "}
                <strong>"{deleteConfirmation.installation.name}"</strong>?
              </p>
              {deleteConfirmation.deviceCount > 0 && (
                <div className="delete-warning">
                  ⚠️ This site has {deleteConfirmation.deviceCount}{" "}
                  {deleteConfirmation.deviceCount === 1 ? "device" : "devices"}.
                  You must remove all devices before deleting.
                </div>
              )}
              <p className="delete-note">This action cannot be undone.</p>

              {deleteConfirmation.deviceCount === 0 && (
                <div
                  className="typed-confirmation-section"
                  style={{ marginTop: "20px" }}
                >
                  <p className="typed-confirmation-instruction">
                    Type <strong>{deleteConfirmation.installation.name}</strong>{" "}
                    to confirm:
                  </p>
                  <input
                    type="text"
                    className="typed-confirmation-input"
                    value={deleteConfirmation.typedName || ""}
                    onChange={(e) =>
                      setDeleteConfirmation({
                        ...deleteConfirmation,
                        typedName: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !loading &&
                        deleteConfirmation.deviceCount === 0 &&
                        deleteConfirmation.typedName ===
                          deleteConfirmation.installation.name
                      ) {
                        confirmDelete();
                      }
                    }}
                    placeholder={`Type "${deleteConfirmation.installation.name}"`}
                    autoFocus
                    disabled={loading}
                  />
                </div>
              )}
            </div>
            <div className="delete-confirmation-actions">
              <button
                className="cancel-button"
                onClick={(e) => {
                  e.stopPropagation();
                  logger.log("[Delete Confirmation] Cancel button clicked");
                  setDeleteConfirmation(null);
                  logger.log(
                    "[Delete Confirmation] After setDeleteConfirmation(null)"
                  );
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={confirmDelete}
                disabled={
                  loading ||
                  deleteConfirmation.deviceCount > 0 ||
                  (deleteConfirmation.deviceCount === 0 &&
                    deleteConfirmation.typedName !==
                      deleteConfirmation.installation.name)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Installation Creation Status Modal */}
      {creationModal.isOpen && (
        <div
          className="creation-modal-overlay"
          onClick={(e) => {
            if (!creationModal.isLoading && creationModal.result) {
              e.stopPropagation();
              // On success, show switch prompt
              if (creationModal.result.type === "success") {
                setPendingSwitch({
                  installationId: creationModal.result.installationId,
                  name: creationModal.result.installationName,
                });
              }
              setCreationModal({
                isOpen: false,
                isLoading: false,
                result: null,
              });
            }
          }}
        >
          <div
            className="creation-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {creationModal.isLoading ? (
              <>
                <div className="creation-spinner"></div>
                <p className="creation-message">Creating site...</p>
              </>
            ) : creationModal.result ? (
              <>
                <div className={`creation-icon ${creationModal.result.type}`}>
                  {creationModal.result.type === "success" ? "✓" : "✕"}
                </div>
                <p className={`creation-message ${creationModal.result.type}`}>
                  {creationModal.result.message}
                </p>
                <button
                  className="creation-close-button"
                  onClick={() => {
                    // On success, show switch prompt
                    if (creationModal.result.type === "success") {
                      setPendingSwitch({
                        installationId: creationModal.result.installationId,
                        name: creationModal.result.installationName,
                      });
                    }
                    setCreationModal({
                      isOpen: false,
                      isLoading: false,
                      result: null,
                    });
                  }}
                >
                  Close
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Installation Deletion Status Modal */}
      {deletionModal.isOpen && (
        <div
          className="deletion-modal-overlay"
          onClick={(e) => {
            if (
              !deletionModal.isLoading &&
              !deletionModal.isRefreshing &&
              deletionModal.result
            ) {
              e.stopPropagation();
              // If deleted current installation, close modal and trigger change installation
              if (deletionModal.result.isDeletingCurrent) {
                setDeletionModal({
                  isOpen: false,
                  isLoading: false,
                  isRefreshing: false,
                  result: null,
                  isDeletingCurrent: false,
                });
                onClose();
                if (onChangeInstallation) {
                  onChangeInstallation(true); // Pass true to indicate forced change (deleted current)
                }
              } else {
                setDeletionModal({
                  isOpen: false,
                  isLoading: false,
                  isRefreshing: false,
                  result: null,
                  isDeletingCurrent: false,
                });
              }
            }
          }}
        >
          <div
            className="deletion-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {deletionModal.isLoading ? (
              <>
                <div className="deletion-spinner"></div>
                <p className="deletion-message">Deleting site...</p>
              </>
            ) : deletionModal.isRefreshing ? (
              <>
                <div className="deletion-spinner"></div>
                <p className="deletion-message">Loading sites...</p>
              </>
            ) : deletionModal.result ? (
              <>
                <div className={`deletion-icon ${deletionModal.result.type}`}>
                  {deletionModal.result.type === "success" ? "✓" : "✕"}
                </div>
                <p className={`deletion-message ${deletionModal.result.type}`}>
                  {deletionModal.result.message}
                </p>
                {deletionModal.result.isDeletingCurrent &&
                  deletionModal.result.type === "success" && (
                    <p className="deletion-submessage">
                      You will be redirected to select a new site.
                    </p>
                  )}
                <button
                  className="deletion-close-button"
                  onClick={() => {
                    // If deleted current installation, close modal and trigger change installation
                    if (deletionModal.result.isDeletingCurrent) {
                      setDeletionModal({
                        isOpen: false,
                        isLoading: false,
                        isRefreshing: false,
                        result: null,
                        isDeletingCurrent: false,
                      });
                      onClose();
                      if (onChangeInstallation) {
                        onChangeInstallation(true); // Pass true to indicate forced change (deleted current)
                      }
                    } else {
                      setDeletionModal({
                        isOpen: false,
                        isLoading: false,
                        isRefreshing: false,
                        result: null,
                        isDeletingCurrent: false,
                      });
                    }
                  }}
                >
                  {deletionModal.result.isDeletingCurrent
                    ? "Continue"
                    : "Close"}
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallationManagementModal;
